import { useTranslation } from 'react-i18next';
import { Dialog } from 'components/shared';
import {
  CorrectTravelMovementPerDayRequest,
  ExpenseClaimResponse,
  ExpenseClaimRouteResponse,
  TravelMovementCorrectionRequest,
  TravelMovementPerDayResponse,
  TravelMovementResponse
} from 'generated/index';
import { Box } from '@mui/material';
import { formatDate } from 'utils/date';
import { AzureMapsProvider } from 'react-azure-maps';
import MapController from 'components/shared/Map/MapController';
import { LatitudeLongitudePair } from 'components/roster-portal/activity-planning/types';
import TravelDetailDialogOverview from './TravelDetailDialogOverview';
import TravelDetailDiaglogRoutes from './TravelDetailDiaglogRoutes';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { mutations } from 'RestAPIs/rapini';
import { FormProvider, useForm } from 'react-hook-form';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import TravelDetailDialogRoutesHeader from './TravelDetailDialogRoutesHeader';

type Props = {
  claim?: ExpenseClaimResponse | TravelMovementPerDayResponse | null;
  isOpen: boolean;
  onClose?: () => void;
  isTravelMovements?: boolean;
};

const TravelDetailDialog = ({
  claim,
  isOpen,
  onClose,
  isTravelMovements = false
}: Props) => {
  const { t } = useTranslation('common');
  const [isEditMode, setEditMode] = useState(false);
  const [editedDistances, setEditedDistances] = useState<{
    [key: number]: number;
  }>({});
  const methods = useForm();

  const queryClient = useQueryClient();

  const { mutate: correctTravelMovement, isLoading } =
    mutations.useCorrectTravelMovementPerDay({
      onSuccess: () => {
        queryClient.invalidateQueries('findTravelMovementsByEmployee');
        handleClose();
        showSuccessSnackbar();
      },
      onError: () => {
        showErrorSnackbar();
      }
    });

  const employmentId = claim?.employment?.id ?? null;
  const date = claim?.date ?? null;
  const isRealised = claim?.status ? claim.status === 'REALIZED' : false;

  if (!claim) return null;

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
    setEditedDistances({});
  };

  const handleDistanceChange = (index: number, value: string) => {
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      setEditedDistances((prev) => ({
        ...prev,
        [index]: numericValue
      }));
    }
  };

  const handleSave = () => {
    if (
      !employmentId ||
      !date ||
      !claim ||
      !isTravelMovements ||
      !isTravelMovement(claim)
    ) {
      showErrorSnackbar();
      return;
    }

    const travelMovementCorrections = claim.travelMovements
      .map((movement, index) => {
        const correctedDistanceInMeters = editedDistances[index]
          ? editedDistances[index] * 1000 // Convert kilometers to meters
          : undefined;

        if (!correctedDistanceInMeters) return null;

        return {
          correctedDistanceInMeters,
          destinationEventId:
            index === 0 ? movement.destinationEventId : undefined,
          departureEventId: index > 0 ? movement.departureEventId : undefined
        };
      })
      .filter(
        (correction) => correction !== null
      ) as TravelMovementCorrectionRequest[];

    const payload: CorrectTravelMovementPerDayRequest = {
      employmentId,
      date,
      travelMovementCorrections
    };

    correctTravelMovement(payload);
  };

  // Type guards
  const isExpenseClaim = (claim): claim is ExpenseClaimResponse =>
    'routes' in claim;
  const isTravelMovement = (claim): claim is TravelMovementPerDayResponse =>
    'travelMovements' in claim;

  const isManual = !!(claim as TravelMovementPerDayResponse).isManual;
  const isCorrected = !!(claim as TravelMovementPerDayResponse).isCorrected;

  const routes: (ExpenseClaimRouteResponse | TravelMovementResponse)[] =
    isTravelMovements && isTravelMovement(claim)
      ? claim.travelMovements || []
      : isExpenseClaim(claim)
      ? claim.routes || []
      : [];

  // Add missing locations to routes when calculate business routes is disabled
  const processedRoutes = routes.reduce(
    (
      acc: (ExpenseClaimRouteResponse | TravelMovementResponse)[],
      current,
      index
    ) => {
      if (index > 0) {
        const previous = acc[acc.length - 1];
        if (
          'toAddress' in previous &&
          'fromAddress' in current &&
          previous.toAddress !== current.fromAddress
        ) {
          acc.push({
            fromAddress: previous.toAddress,
            toAddress: current.fromAddress,
            fromDescription: previous.toDescription,
            toDescription: current.fromDescription,
            distanceInMeters: 0,
            type: 'BUSINESS'
          });
        }
      }
      acc.push(current);
      return acc;
    },
    []
  );

  const pointSet = routes.map(
    ({ points }) => JSON.parse(points || '[]') as LatitudeLongitudePair[]
  );

  const handleClose = () => {
    onClose?.();
    setEditedDistances({});
    setEditMode(false);
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        open={isOpen && !!claim}
        title={`${t('travelExpenseClaims.details')} - ${formatDate(
          claim.date
        )} - ${claim.employment.employeeName}`}
        handleClose={handleClose}
        maxWidth="sm"
        PaperProps={{
          sx: {
            minWidth: '75%',
            minHeight: '75%',
            maxWidth: '600px',
            maxHeight: 'calc(100% - 200px)'
          }
        }}
      >
        <Box
          display="flex"
          gap={2}
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <Box
            sx={{
              width: '50%',
              minWidth: '500px'
            }}
          >
            <TravelDetailDialogOverview
              claim={claim}
              routes={routes}
              isTravelMovements={isTravelMovements}
              isManual={isManual}
              isCorrected={isCorrected}
            />

            <TravelDetailDialogRoutesHeader
              routes={routes}
              isTravelMovements={isTravelMovements}
              isManual={isManual}
              isEditMode={isEditMode}
              toggleEditMode={toggleEditMode}
              handleSave={handleSave}
              isLoading={isLoading}
              isRealised={isRealised}
            />

            <Box
              sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                maxHeight: '500px'
              }}
            >
              {processedRoutes.map((route, i) => {
                const isLast = isTravelMovements
                  ? processedRoutes.length === i + 1
                  : isExpenseClaim(claim) && claim.routes.length === i + 1;

                return (
                  <TravelDetailDiaglogRoutes
                    key={i}
                    route={route}
                    isTravelMovements={isTravelMovements}
                    isLast={isLast}
                    i={i}
                    isManual={isManual}
                    isEditMode={isEditMode}
                    editedDistance={editedDistances[i]}
                    onDistanceChange={handleDistanceChange}
                  />
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              width: '50%',
              minHeight: '750px',
              minWidth: '500px'
            }}
          >
            <AzureMapsProvider>
              <MapController pointSet={pointSet} />
            </AzureMapsProvider>
          </Box>
        </Box>
      </Dialog>
    </FormProvider>
  );
};

export default TravelDetailDialog;
